import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, RouterStateSnapshot, UrlTree } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { AdminRoleGuard } from '@bodyanalytics/ui-core';
import { firstValueFrom } from 'rxjs';

/**
 * #2303 — a hard reload of a deep admin link (e.g. `/auth/manage-events`) must
 * return the user to that route once auth completes, not the dashboard.
 *
 * `AdminRoleGuard`'s unauthenticated branch just parks the user on `/login`
 * with no memory of `state.url`; the "Log in" button there then calls
 * `loginWithRedirect()` with no `appState`, so `@auth0/auth0-angular`'s
 * default redirect target is `/` and the shared `LoginComponent` overrides
 * that with a hardcoded dashboard route regardless of the original deep link.
 *
 * This wraps `AdminRoleGuard`: on the unauthenticated path it kicks off Auth0
 * directly with `appState.target` set to the attempted URL — the same
 * pattern the customer portal's `customerIdentityGuard` uses. Once login
 * completes, `AuthService.handleRedirectCallback` restores that target via
 * its Router-aware navigator with no further wiring needed. An authenticated
 * request falls straight through to the existing role check, unchanged.
 */
export const adminDeepLinkGuard: CanActivateFn = async (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
): Promise<boolean | UrlTree> => {
  const authService = inject(AuthService);
  const adminRoleGuard = inject(AdminRoleGuard);

  const isAuthenticated = await firstValueFrom(authService.isAuthenticated$);
  if (!isAuthenticated) {
    authService.loginWithRedirect({ appState: { target: state.url } });
    return false;
  }

  return adminRoleGuard.canActivate(route, state);
};
